import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useTheme, useAuth, API } from "@/App";
import DrishtiPaywallGate from "@/components/drishti/DrishtiPaywallGate";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Search, Calculator, Shield, Target, TrendingUp, TrendingDown,
  Zap, Info, Copy, Check, BarChart3, PieChart,
  AlertTriangle, CheckCircle2, Percent, ArrowRight,
  HelpCircle, RefreshCw, Flame, BookOpen, Layers, Coins, Scale,
  X, Radio, Sparkles
} from "lucide-react";
import { toast } from "sonner";

// ── Fallback Drishti Alerts if backend is loading ───────────────────────────
const FALLBACK_ALERTS = [
  { alert_id: "demo-1", script_name: "PNBHOUSING", zone_type: "DEMAND", entry: 1091.9, stop_loss: 1051.9, target: 1172.0, timeframe: "5 Min", score: "9.2" },
  { alert_id: "demo-2", script_name: "RELIANCE", zone_type: "DEMAND", entry: 2950.0, stop_loss: 2910.0, target: 3040.0, timeframe: "15 Min", score: "8.8" },
  { alert_id: "demo-3", script_name: "TATASTEEL", zone_type: "SUPPLY", entry: 154.5, stop_loss: 158.0, target: 147.0, timeframe: "15 Min", score: "8.5" },
  { alert_id: "demo-4", script_name: "HDFCBANK", zone_type: "DEMAND", entry: 1680.0, stop_loss: 1655.0, target: 1735.0, timeframe: "1 Hour", score: "9.0" },
  { alert_id: "demo-5", script_name: "INFY", zone_type: "SUPPLY", entry: 1820.0, stop_loss: 1845.0, target: 1770.0, timeframe: "15 Min", score: "8.4" }
];

// ── Popular Stocks for Quick Search ───────────────────────────────────────────
const POPULAR_STOCKS = [
  { symbol: "RELIANCE", name: "Reliance Industries", defaultLot: 250, defaultPrice: 2950 },
  { symbol: "HDFCBANK", name: "HDFC Bank", defaultLot: 550, defaultPrice: 1680 },
  { symbol: "TATASTEEL", name: "Tata Steel", defaultLot: 5500, defaultPrice: 154 },
  { symbol: "INFY", name: "Infosys Ltd", defaultLot: 400, defaultPrice: 1820 },
  { symbol: "ICICIBANK", name: "ICICI Bank", defaultLot: 700, defaultPrice: 1240 },
  { symbol: "TCS", name: "Tata Consultancy Services", defaultLot: 175, defaultPrice: 4120 },
  { symbol: "SBIN", name: "State Bank of India", defaultLot: 750, defaultPrice: 785 },
  { symbol: "PNBHOUSING", name: "PNB Housing Finance", defaultLot: 800, defaultPrice: 1090 },
  { symbol: "BHARTIARTL", name: "Bharti Airtel", defaultLot: 475, defaultPrice: 1610 },
  { symbol: "ITC", name: "ITC Ltd", defaultLot: 1600, defaultPrice: 475 },
  { symbol: "LT", name: "Larsen & Toubro", defaultLot: 150, defaultPrice: 3580 },
  { symbol: "ZOMATO", name: "Zomato Ltd", defaultLot: 2000, defaultPrice: 260 },
  { symbol: "TRENT", name: "Trent Ltd", defaultLot: 100, defaultPrice: 7100 },
];

const CAPITAL_PRESETS = [25000, 50000, 100000, 200000, 500000, 1000000];
const RISK_PRESETS = [0.5, 1, 1.5, 2, 3, 5];

export default function TradingCalculatorPage() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isLight = theme === "light";
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const [hasAccess, setHasAccess] = useState(isAdmin || !!user?.has_drishti_access);

  useEffect(() => {
    if (isAdmin) {
      setHasAccess(true);
      return;
    }
    axios.get(`${API}/drishti/subscription-status`, { withCredentials: true })
      .then(res => {
        setHasAccess(Boolean(res.data?.has_access));
      })
      .catch(() => {
        setHasAccess(false);
      });
  }, [isAdmin, user]);

  const [activeTab, setActiveTab] = useState("position-size"); // 'position-size' | 'expectancy' | 'drawdown' | 'guide'

  // ════════════════════════════════════════════════════════════════════════════
  // 1. POSITION SIZING STATE (ONLY SPECIFIC STOCKS OR DRISHTI ALERTS)
  // ════════════════════════════════════════════════════════════════════════════
  const [stockSource, setStockSource] = useState("alert"); // 'alert' | 'search'
  const [activeAlerts, setActiveAlerts] = useState(FALLBACK_ALERTS);
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  // Selected Stock Details
  const [selectedStock, setSelectedStock] = useState({
    symbol: "PNBHOUSING",
    companyName: "PNB Housing Finance",
    instrumentType: "equity", // 'equity' | 'fno'
    lotSize: 1, // 1 for cash equity
    isAlert: true,
    alertId: "demo-1",
    zoneType: "DEMAND",
    timeframe: "5 Min"
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [capital, setCapital] = useState(100000);
  const [riskPct, setRiskPct] = useState(2);
  const [entryPrice, setEntryPrice] = useState(1091.9);
  const [slPrice, setSlPrice] = useState(1051.9);
  const [targetPrice, setTargetPrice] = useState(1172.0);
  const [tradeDirection, setTradeDirection] = useState("BUY"); // 'BUY' | 'SELL'
  const [copiedPlan, setCopiedPlan] = useState(false);

  // Fetch live Drishti alerts on mount
  useEffect(() => {
    let isMounted = true;
    const fetchAlerts = async () => {
      try {
        setLoadingAlerts(true);
        const res = await axios.get(`${API}/drishti/alerts`, { withCredentials: true, timeout: 6000 });
        if (isMounted && res.data) {
          const raw = Array.isArray(res.data) ? res.data : (res.data.alerts || []);
          if (raw.length > 0) {
            setActiveAlerts(raw);
          }
        }
      } catch (err) {
        // Fallback alerts remain intact
      } finally {
        if (isMounted) setLoadingAlerts(false);
      }
    };
    fetchAlerts();
    return () => { isMounted = false; };
  }, []);

  // Handler to pick an active Drishti alert
  const handleSelectAlert = (alert) => {
    const isDemand = (alert.zone_type || "DEMAND").toUpperCase() === "DEMAND";
    const e = parseFloat(alert.entry) || 100;
    const s = parseFloat(alert.stop_loss) || (isDemand ? e * 0.96 : e * 1.04);
    const t = parseFloat(alert.target) || (isDemand ? e * 1.08 : e * 0.92);

    setSelectedStock({
      symbol: alert.script_name,
      companyName: alert.script_name,
      instrumentType: alert.instrument_type || "equity",
      lotSize: alert.lot_size || 1,
      isAlert: true,
      alertId: alert.alert_id,
      zoneType: alert.zone_type || (isDemand ? "DEMAND" : "SUPPLY"),
      timeframe: alert.timeframe || "15 Min"
    });

    setEntryPrice(e);
    setSlPrice(s);
    setTargetPrice(t);
    setTradeDirection(isDemand ? "BUY" : "SELL");
    toast.success(`Loaded active alert for ${alert.script_name} (${alert.zone_type || "DEMAND"})`);
  };

  // Handler to pick a searched stock
  const handleSelectSearchedStock = (item, type = "equity") => {
    const sym = typeof item === "string" ? item.toUpperCase() : item.symbol.toUpperCase();
    const name = typeof item === "string" ? item.toUpperCase() : (item.name || item.symbol);
    const defaultLot = typeof item === "object" ? (item.defaultLot || 250) : 250;
    const defaultPrice = typeof item === "object" ? (item.defaultPrice || 1000) : 1000;
    const lot = type === "equity" ? 1 : defaultLot;

    setSelectedStock({
      symbol: sym,
      companyName: name,
      instrumentType: type,
      lotSize: lot,
      isAlert: false,
      alertId: null,
      zoneType: null,
      timeframe: null
    });

    setEntryPrice(defaultPrice);
    setSlPrice(Number((defaultPrice * 0.96).toFixed(2)));
    setTargetPrice(Number((defaultPrice * 1.08).toFixed(2)));
    setTradeDirection("BUY");
    setSearchQuery("");
    toast.success(`Selected ${sym} (${type === "equity" ? "Cash Equity" : `F&O Lot: ${lot}`})`);
  };

  // Position Calculations
  const positionResults = useMemo(() => {
    const numCap = Number(capital) || 0;
    const numRiskPct = Number(riskPct) || 0;
    const numEntry = Number(entryPrice) || 0;
    const numSl = Number(slPrice) || 0;
    const numTarget = Number(targetPrice) || 0;
    const isEquity = selectedStock.instrumentType === "equity";
    const lotSize = Math.max(1, Number(selectedStock.lotSize) || 1);

    const maxRiskAmount = (numCap * numRiskPct) / 100;
    const pointRisk = Math.abs(numEntry - numSl);
    const pointReward = Math.abs(numTarget - numEntry);

    let recommendedQty = 0;
    let recommendedLots = 0;

    if (pointRisk > 0) {
      const rawQty = maxRiskAmount / pointRisk;
      if (isEquity) {
        recommendedQty = Math.floor(rawQty);
        recommendedLots = recommendedQty;
      } else {
        recommendedLots = Math.floor(rawQty / lotSize);
        if (recommendedLots < 1 && rawQty >= lotSize * 0.5) {
          recommendedLots = 1;
        }
        recommendedQty = recommendedLots * lotSize;
      }
    }

    const orderValue = recommendedQty * numEntry;
    const actualRisk = recommendedQty * pointRisk;
    const actualReward = recommendedQty * pointReward;
    const rrRatio = pointRisk > 0 ? pointReward / pointRisk : 0;
    const capitalUtilizationPct = numCap > 0 ? (orderValue / numCap) * 100 : 0;
    const returnOnCapitalPct = numCap > 0 ? (actualReward / numCap) * 100 : 0;
    const lossOnCapitalPct = numCap > 0 ? (actualRisk / numCap) * 100 : 0;

    const isOverLeveraged = capitalUtilizationPct > 100;
    const isHighRisk = numRiskPct > 3;

    return {
      maxRiskAmount,
      pointRisk,
      pointReward,
      recommendedLots,
      recommendedQty,
      orderValue,
      actualRisk,
      actualReward,
      rrRatio,
      capitalUtilizationPct,
      returnOnCapitalPct,
      lossOnCapitalPct,
      isOverLeveraged,
      isHighRisk,
      isEquity,
      lotSize
    };
  }, [capital, riskPct, entryPrice, slPrice, targetPrice, selectedStock]);

  const copyTradePlan = () => {
    const text = `🎯 **JAMADHAN RISK & POSITION PLAN**
────────────────────────────
• Stock / Symbol: ${selectedStock.symbol} (${tradeDirection})
• Execution Mode: ${positionResults.isEquity ? "Cash Equity (Shares)" : `F&O Derivatives (Lot: ${positionResults.lotSize})`}
${selectedStock.isAlert ? `• Drishti Alert: Linked (${selectedStock.zoneType || "Demand"})` : ""}
• Entry Price: ₹${entryPrice}
• Stop Loss: ₹${slPrice} (-${positionResults.pointRisk.toFixed(2)} pts)
• Target Price: ₹${targetPrice} (+${positionResults.pointReward.toFixed(2)} pts)
• R:R Ratio: 1 : ${positionResults.rrRatio.toFixed(2)}
────────────────────────────
• Recommended Order: ${positionResults.isEquity ? `${positionResults.recommendedQty} Shares` : `${positionResults.recommendedLots} Lots (${positionResults.recommendedQty} Qty)`}
• Required Capital: ₹${positionResults.orderValue.toLocaleString("en-IN")}
• Max Risk (Stop Loss): ₹${positionResults.actualRisk.toLocaleString("en-IN")} (${riskPct}%)
• Potential Reward: ₹${positionResults.actualReward.toLocaleString("en-IN")}
────────────────────────────
Strictly disciplined execution via Jamadhan Terminal. Zero emotional sizing.`;

    navigator.clipboard.writeText(text);
    setCopiedPlan(true);
    toast.success(`Trade Plan for ${selectedStock.symbol} copied!`);
    setTimeout(() => setCopiedPlan(false), 2500);
  };

  // ════════════════════════════════════════════════════════════════════════════
  // 2. EXPECTANCY & EV MATRIX STATE
  // ════════════════════════════════════════════════════════════════════════════
  const [winRate, setWinRate] = useState(50); // %
  const [rrExpectancy, setRrExpectancy] = useState(2.0); // 1:2
  const [baseRisk, setBaseRisk] = useState(2000); // ₹
  const [sampleTrades, setSampleTrades] = useState(50);

  const expectancyResults = useMemo(() => {
    const pWin = winRate / 100;
    const pLoss = 1 - pWin;
    const winAmt = baseRisk * rrExpectancy;
    const lossAmt = baseRisk;

    // EV = (P_win * WinAmt) - (P_loss * LossAmt)
    const evPerTrade = (pWin * winAmt) - (pLoss * lossAmt);
    const totalExpectedProfit = evPerTrade * sampleTrades;
    const breakevenWinRate = (1 / (1 + rrExpectancy)) * 100;
    const profitFactor = (pWin * winAmt) / (pLoss * lossAmt || 1);

    const winningTrades = Math.round(sampleTrades * pWin);
    const losingTrades = sampleTrades - winningTrades;
    const totalGrossWins = winningTrades * winAmt;
    const totalGrossLosses = losingTrades * lossAmt;

    return {
      evPerTrade,
      totalExpectedProfit,
      breakevenWinRate,
      profitFactor,
      winAmt,
      lossAmt,
      winningTrades,
      losingTrades,
      totalGrossWins,
      totalGrossLosses,
    };
  }, [winRate, rrExpectancy, baseRisk, sampleTrades]);

  // ════════════════════════════════════════════════════════════════════════════
  // 3. CAPITAL SHIELD & DRAWDOWN STATE
  // ════════════════════════════════════════════════════════════════════════════
  const [shieldCapital, setShieldCapital] = useState(100000);
  const [maxDrawdownPct, setMaxDrawdownPct] = useState(10);
  const [dailyLossLimitPct, setDailyLossLimitPct] = useState(3);
  const [riskPerTradePct, setRiskPerTradePct] = useState(1.5);

  const shieldResults = useMemo(() => {
    const capNum = Number(shieldCapital) || 0;
    const maxDrawdownAmt = (capNum * maxDrawdownPct) / 100;
    const dailyLossAmt = (capNum * dailyLossLimitPct) / 100;
    const singleTradeRiskAmt = (capNum * riskPerTradePct) / 100;

    // Consecutive losses before drawdown limit reached
    const consecutiveLosses = singleTradeRiskAmt > 0 ? Math.floor(maxDrawdownAmt / singleTradeRiskAmt) : 0;
    const maxTradesPerDay = singleTradeRiskAmt > 0 ? Math.floor(dailyLossAmt / singleTradeRiskAmt) : 0;

    return {
      maxDrawdownAmt,
      dailyLossAmt,
      singleTradeRiskAmt,
      consecutiveLosses,
      maxTradesPerDay,
    };
  }, [shieldCapital, maxDrawdownPct, dailyLossLimitPct, riskPerTradePct]);

  // Gate the entire page if user does not have active access
  if (!hasAccess && !isAdmin) {
    return (
      <DashboardLayout title="Risk & Position Sizing Calculator">
        <DrishtiPaywallGate
          featureName="Risk & Lot Size Calculator"
          onAccessGranted={() => setHasAccess(true)}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Risk & Position Sizing Calculator">
      <div className="space-y-6 max-w-7xl mx-auto pb-12" data-testid="trading-calculator-page">
        {/* ═══ Header Banner ═══ */}
        <div
          className={`relative overflow-hidden rounded-2xl p-6 md:p-8 border transition-all ${isLight
              ? "bg-white border-gray-200 shadow-sm"
              : "bg-gradient-to-r from-[#0C0E14] via-[#101420] to-[#0A0C12] border-white/[0.08] shadow-2xl"
            }`}
        >
          <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-[#00FF88]/10 blur-3xl pointer-events-none" />
          <div className="absolute right-32 -bottom-16 w-64 h-64 rounded-full bg-[#00F0FF]/10 blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black font-mono tracking-widest uppercase bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/30">
                  INSTITUTIONAL RISK DESK
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold font-mono uppercase bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30">
                  MATHEMATICAL EDGE
                </span>
              </div>
              <h1 className={`text-2xl md:text-3xl font-black font-outfit tracking-tight ${isLight ? "text-gray-900" : "text-white"}`}>
                Risk & Position Sizing Engine
              </h1>
              <p className={`text-sm font-plex mt-1 max-w-2xl ${isLight ? "text-gray-600" : "text-[#94A3B8]"}`}>
                Calculate exact lot quantities, safe capital allocation, positive EV expectancy, and true net brokerage before entering any trade. Eliminate emotion with math.
              </p>
            </div>

            {/* Quick action buttons */}
            <div className="flex items-center gap-2.5 shrink-0 flex-wrap">
              <Link to="/drishti">
                <Button
                  className="bg-[#00FF88] text-black font-bold font-outfit text-xs px-4 h-9 rounded-xl hover:brightness-110 shadow-[0_0_20px_rgba(0,255,136,0.25)]"
                >
                  <Zap className="w-3.5 h-3.5 mr-1.5" /> View Drishti Alerts
                </Button>
              </Link>
              <Link to="/journal">
                <Button
                  variant="outline"
                  className={`font-outfit text-xs px-4 h-9 rounded-xl border ${isLight ? "border-gray-300 text-gray-800" : "border-white/10 text-white hover:bg-white/[0.05]"
                    }`}
                >
                  <BarChart3 className="w-3.5 h-3.5 mr-1.5 text-[#00F0FF]" /> Trade Journal
                </Button>
              </Link>
            </div>
          </div>

          {/* Nav Tabs */}
          <div className="flex items-center gap-1.5 mt-6 pt-4 border-t border-white/[0.06] overflow-x-auto custom-scrollbar">
            {[
              { id: "position-size", label: "Position Size & Lots", icon: Target },
              { id: "expectancy", label: "R:R & Expected Value (EV)", icon: Scale },
              { id: "drawdown", label: "Capital Shield & Drawdown", icon: Shield },
              { id: "guide", label: "Pro Money Management Rules", icon: BookOpen },
            ].map((tab) => {
              const active = activeTab === tab.id;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold font-outfit whitespace-nowrap transition-all ${active
                      ? isLight
                        ? "bg-[#6366F1] text-white shadow-md"
                        : "bg-[#00FF88] text-black shadow-[0_0_15px_rgba(0,255,136,0.25)]"
                      : isLight
                        ? "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                        : "text-[#94A3B8] hover:text-white hover:bg-white/[0.04]"
                    }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════════
            TAB 1: POSITION SIZING & LOT CALCULATOR
            ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === "position-size" && (
          <div className="grid grid-cols-1 md:grid-cols-12 lg:grid-cols-12 gap-6">
            {/* ── Inputs Panel (7 Cols) ── */}
            <div
              className={`md:col-span-7 lg:col-span-7 rounded-2xl p-6 border ${isLight ? "bg-white border-gray-200" : "bg-[#0E1017] border-white/[0.08]"
                } space-y-5`}
            >
              <div className="flex items-center justify-between pb-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-[#00FF88]" />
                  <h3 className={`text-base font-bold font-outfit ${isLight ? "text-gray-900" : "text-white"}`}>
                    Trade Parameters & Risk Inputs
                  </h3>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setTradeDirection("BUY")}
                    className={`px-3 py-1 rounded-lg text-xs font-bold font-mono transition-all ${tradeDirection === "BUY"
                        ? "bg-[#00FF88] text-black shadow-sm"
                        : "text-gray-400 hover:text-white"
                      }`}
                  >
                    BUY / LONG
                  </button>
                  <button
                    onClick={() => setTradeDirection("SELL")}
                    className={`px-3 py-1 rounded-lg text-xs font-bold font-mono transition-all ${tradeDirection === "SELL"
                        ? "bg-[#FF3B30] text-white shadow-sm"
                        : "text-gray-400 hover:text-white"
                      }`}
                  >
                    SELL / SHORT
                  </button>
                </div>
              </div>

              {/* 1. Specific Stock Selection: Drishti Alert OR Market Search */}
              <div className="space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <label className={`text-xs font-mono uppercase font-semibold flex items-center gap-1.5 ${isLight ? "text-gray-700" : "text-gray-300"}`}>
                    <Sparkles className="w-3.5 h-3.5 text-[#00FF88]" />
                    1. Choose Stock or Active Drishti Alert
                  </label>
                  {/* Mode Toggle: Alert vs Search */}
                  <div className="flex items-center gap-1 bg-white/[0.04] p-1 rounded-lg border border-white/[0.08]">
                    <button
                      type="button"
                      onClick={() => setStockSource("alert")}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-mono font-bold flex items-center gap-1.5 transition-all ${stockSource === "alert"
                          ? "bg-[#00FF88] text-black shadow-sm"
                          : "text-gray-400 hover:text-white"
                        }`}
                    >
                      <Flame className="w-3 h-3" />
                      Active Alerts ({activeAlerts.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setStockSource("search")}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-mono font-bold flex items-center gap-1.5 transition-all ${stockSource === "search"
                          ? "bg-[#00F0FF] text-black shadow-sm"
                          : "text-gray-400 hover:text-white"
                        }`}
                    >
                      <Search className="w-3 h-3" />
                      Search Market Stock
                    </button>
                  </div>
                </div>

                {/* Subview A: Drishti Active Alerts Selector */}
                {stockSource === "alert" && (
                  <div className="space-y-2">
                    <p className="text-[11px] font-plex text-gray-400">
                      Select an institutional Drishti alert to auto-fill verified entry, stop loss, and target levels:
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {activeAlerts.map((alt) => {
                        const isSelected = selectedStock.symbol === alt.script_name && selectedStock.isAlert;
                        const isDemand = (alt.zone_type || "DEMAND").toUpperCase() === "DEMAND";
                        return (
                          <button
                            key={alt.alert_id || alt.script_name}
                            type="button"
                            onClick={() => handleSelectAlert(alt)}
                            className={`p-2.5 rounded-xl border text-left transition-all relative ${isSelected
                                ? isLight
                                  ? "bg-emerald-50 border-emerald-500 shadow-md ring-1 ring-emerald-500"
                                  : "bg-[#00FF88]/10 border-[#00FF88] shadow-[0_0_15px_rgba(0,255,136,0.2)] ring-1 ring-[#00FF88]"
                                : isLight
                                  ? "bg-gray-50 border-gray-200 hover:bg-gray-100"
                                  : "bg-white/[0.02] border-white/[0.06] hover:border-white/20 hover:bg-white/[0.05]"
                              }`}
                          >
                            <div className="flex items-center justify-between gap-1 mb-1">
                              <span className={`font-outfit font-black text-sm tracking-tight ${isLight ? "text-gray-900" : "text-white"}`}>
                                {alt.script_name}
                              </span>
                              <Badge
                                className={`text-[9px] px-1.5 py-0 font-mono font-bold uppercase border-0 ${isDemand
                                    ? "bg-[#00FF88]/20 text-[#00FF88]"
                                    : "bg-[#FF3B30]/20 text-[#FF3B30]"
                                  }`}
                              >
                                {alt.zone_type || "DEMAND"}
                              </Badge>
                            </div>
                            <div className={`flex items-center justify-between text-[10px] font-mono ${isLight ? "text-gray-600 font-semibold" : "text-gray-400"}`}>
                              <span>Entry: ₹{alt.entry}</span>
                              <span className="text-[#FF3B30]">SL: ₹{alt.stop_loss}</span>
                              <span className={isLight ? "text-emerald-700 font-bold" : "text-[#00FF88]"}>Tgt: ₹{alt.target}</span>
                            </div>
                            {isSelected && (
                              <div className={`mt-1 text-[9px] font-mono font-bold flex items-center gap-1 ${isLight ? "text-emerald-700" : "text-[#00FF88]"}`}>
                                <Check className="w-3 h-3" /> Active Loaded Setup
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Subview B: Search Any Market Stock */}
                {stockSource === "search" && (
                  <div className="space-y-3">
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="Search any stock (e.g. RELIANCE, TATASTEEL, INFY, HDFCBANK)..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={`font-mono text-sm h-10 pl-9 ${isLight
                            ? "bg-white border-gray-300 text-gray-900"
                            : "bg-white/[0.04] border-white/10 text-white"
                          }`}
                      />
                      <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                      {searchQuery && (
                        <button
                          type="button"
                          onClick={() => setSearchQuery("")}
                          className="absolute right-3 top-3 text-gray-400 hover:text-white"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}

                      {/* Instant Search Results Dropdown */}
                      {searchQuery.trim().length > 0 && (
                        <div
                          className={`absolute z-30 w-full mt-1.5 rounded-xl border p-2 shadow-2xl space-y-1 max-h-60 overflow-y-auto custom-scrollbar ${isLight ? "bg-white border-gray-200" : "bg-[#0E1017] border-white/15 backdrop-blur-xl"
                            }`}
                        >
                          {/* Matched popular stocks */}
                          {POPULAR_STOCKS.filter((s) =>
                            s.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            s.name.toLowerCase().includes(searchQuery.toLowerCase())
                          ).map((stock) => (
                            <div
                              key={stock.symbol}
                              className="p-2 rounded-lg hover:bg-white/[0.06] flex items-center justify-between gap-2 border border-transparent hover:border-white/5"
                            >
                              <div>
                                <span className={`font-mono font-bold text-xs ${isLight ? "text-gray-900" : "text-white"}`}>
                                  {stock.symbol}
                                </span>
                                <span className={`text-[10px] ml-2 ${isLight ? "text-gray-600 font-medium" : "text-gray-400"}`}>
                                  {stock.name} • Typical ₹{stock.defaultPrice}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => handleSelectSearchedStock(stock, "equity")}
                                  className="h-6 px-2 text-[10px] font-mono bg-[#00F0FF]/15 text-[#00F0FF] hover:bg-[#00F0FF]/30 border border-[#00F0FF]/30"
                                >
                                  Cash Equity
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => handleSelectSearchedStock(stock, "fno")}
                                  className="h-6 px-2 text-[10px] font-mono bg-[#00FF88]/15 text-[#00FF88] hover:bg-[#00FF88]/30 border border-[#00FF88]/30"
                                >
                                  F&O (Lot: {stock.defaultLot})
                                </Button>
                              </div>
                            </div>
                          ))}

                          {/* Option to use custom typed ticker */}
                          <div className="p-2 rounded-lg bg-white/[0.02] border border-white/10 flex items-center justify-between gap-2 mt-1">
                            <div>
                              <span className="font-mono font-bold text-xs text-[#00FF88]">
                                Custom: "{searchQuery.toUpperCase()}"
                              </span>
                              <span className="text-[10px] text-gray-400 block">
                                Calculate position for custom ticker
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => handleSelectSearchedStock(searchQuery.toUpperCase(), "equity")}
                                className="h-6 px-2 text-[10px] font-mono bg-[#00F0FF]/20 text-[#00F0FF] hover:bg-[#00F0FF]/30"
                              >
                                As Equity
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => handleSelectSearchedStock(searchQuery.toUpperCase(), "fno")}
                                className="h-6 px-2 text-[10px] font-mono bg-[#00FF88]/20 text-[#00FF88] hover:bg-[#00FF88]/30"
                              >
                                As F&O
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Quick Popular Stock Pills */}
                    <div>
                      <p className="text-[10px] font-mono uppercase text-gray-400 mb-1.5">
                        Quick Select Indian Stock:
                      </p>
                      <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar pb-1">
                        {POPULAR_STOCKS.slice(0, 9).map((stock) => {
                          const isSel = selectedStock.symbol === stock.symbol && !selectedStock.isAlert;
                          return (
                            <button
                              key={stock.symbol}
                              type="button"
                              onClick={() => handleSelectSearchedStock(stock, selectedStock.instrumentType || "equity")}
                              className={`px-2.5 py-1.5 rounded-lg border text-xs font-mono font-bold whitespace-nowrap transition-all shrink-0 ${isSel
                                  ? "bg-[#00F0FF]/20 border-[#00F0FF] text-[#00F0FF] shadow-sm"
                                  : "bg-white/[0.03] border-white/[0.08] text-gray-300 hover:border-white/20 hover:text-white"
                                }`}
                            >
                              {stock.symbol}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Active Selected Stock Bar & Customization ── */}
                <div className={`p-3 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${isLight ? "bg-gray-50 border-gray-200" : "bg-gradient-to-r from-white/[0.04] to-white/[0.01] border-white/10"}`}>
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-[#00FF88]/15 border border-[#00FF88]/30 flex items-center justify-center font-bold text-xs text-[#00FF88] font-mono">
                      {selectedStock.symbol.slice(0, 2)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`font-outfit font-black text-sm tracking-wide ${isLight ? "text-gray-900" : "text-white"}`}>
                          {selectedStock.symbol}
                        </span>
                        <Badge
                          className={`text-[9px] font-mono font-bold uppercase border-0 ${selectedStock.instrumentType === "equity"
                              ? "bg-[#00F0FF]/20 text-[#00F0FF]"
                              : "bg-[#00FF88]/20 text-[#00FF88]"
                            }`}
                        >
                          {selectedStock.instrumentType === "equity" ? "Cash Equity" : "F&O Derivatives"}
                        </Badge>
                        {selectedStock.isAlert && (
                          <Badge className="bg-[#A855F7]/20 text-[#A855F7] text-[9px] font-mono border-0">
                            ★ Drishti Alert
                          </Badge>
                        )}
                      </div>
                      <p className={`text-[10px] font-mono mt-0.5 ${isLight ? "text-gray-600 font-medium" : "text-gray-400"}`}>
                        {selectedStock.instrumentType === "equity"
                          ? "1 Unit / Share sizing (Cash market)"
                          : `Derivative Contract • Lot size: ${selectedStock.lotSize} qty`}
                      </p>
                    </div>
                  </div>

                  {/* Mode & Lot Size Selector */}
                  <div className="flex items-center gap-2 shrink-0">
                    <div className={`flex items-center rounded-lg p-0.5 border ${isLight ? "bg-gray-200/70 border-gray-300" : "bg-black/40 border-white/10"}`}>
                      <button
                        type="button"
                        onClick={() => setSelectedStock((p) => ({ ...p, instrumentType: "equity", lotSize: 1 }))}
                        className={`px-2 py-1 text-[10px] font-mono rounded font-bold transition-all ${selectedStock.instrumentType === "equity"
                            ? "bg-[#00F0FF] text-black shadow-sm"
                            : isLight ? "text-gray-600 hover:text-gray-900" : "text-gray-400 hover:text-white"
                          }`}
                      >
                        Equity (Cash)
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedStock((p) => ({ ...p, instrumentType: "fno", lotSize: p.lotSize > 1 ? p.lotSize : 250 }))}
                        className={`px-2 py-1 text-[10px] font-mono rounded font-bold transition-all ${selectedStock.instrumentType === "fno"
                            ? "bg-[#00FF88] text-black shadow-sm"
                            : isLight ? "text-gray-600 hover:text-gray-900" : "text-gray-400 hover:text-white"
                          }`}
                      >
                        F&O Lots
                      </button>
                    </div>

                    {selectedStock.instrumentType === "fno" && (
                      <div className={`flex items-center gap-1.5 font-mono text-xs px-2 py-1 rounded-lg border ${isLight ? "bg-gray-100 border-gray-300" : "bg-white/[0.04] border-white/10"}`}>
                        <span className={`text-[10px] uppercase font-bold ${isLight ? "text-gray-700" : "text-gray-400"}`}>Lot Size:</span>
                        <input
                          type="number"
                          min="1"
                          max="50000"
                          value={selectedStock.lotSize}
                          onChange={(e) => {
                            const val = Math.max(1, Number(e.target.value) || 1);
                            setSelectedStock((p) => ({ ...p, lotSize: val }));
                          }}
                          className={`w-16 rounded px-1.5 py-0.5 text-center text-xs font-bold ${isLight ? "bg-white border border-gray-300 text-gray-900" : "bg-black/60 border border-white/20 text-white"}`}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 2. Capital & Risk Preset */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Total Capital */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className={`text-xs font-mono uppercase font-semibold ${isLight ? "text-gray-700" : "text-gray-300"}`}>
                      2. Total Capital (₹)
                    </label>
                    <span className="text-[10px] font-mono text-[#00FF88] font-bold">
                      ₹{Number(capital).toLocaleString("en-IN")}
                    </span>
                  </div>
                  <Input
                    type="number"
                    value={capital}
                    onChange={(e) => setCapital(Math.max(1000, Number(e.target.value)))}
                    className={`font-mono text-sm h-10 ${isLight ? "bg-white border-gray-300 text-gray-900" : "bg-white/[0.04] border-white/10 text-white"
                      }`}
                    placeholder="100000"
                  />
                  <div className="flex items-center gap-1 mt-1.5 overflow-x-auto custom-scrollbar">
                    {CAPITAL_PRESETS.map((amt) => (
                      <button
                        key={amt}
                        onClick={() => setCapital(amt)}
                        className={`text-[10px] px-2 py-0.5 rounded font-mono transition-all shrink-0 ${capital === amt
                            ? "bg-[#00F0FF] text-black font-bold"
                            : isLight
                              ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                              : "bg-white/[0.04] text-gray-400 hover:bg-white/10 hover:text-white"
                          }`}
                      >
                        ₹{amt >= 100000 ? `${amt / 100000}L` : `${amt / 1000}k`}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Risk % per trade */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className={`text-xs font-mono uppercase font-semibold ${isLight ? "text-gray-700" : "text-gray-300"}`}>
                      3. Max Risk Per Trade (%)
                    </label>
                    <span className={`text-[10px] font-mono font-bold ${riskPct > 2 ? "text-[#FFCC00]" : "text-[#00FF88]"}`}>
                      ₹{positionResults.maxRiskAmount.toLocaleString("en-IN")} ({riskPct}%)
                    </span>
                  </div>
                  <Input
                    type="number"
                    step="0.5"
                    min="0.1"
                    max="20"
                    value={riskPct}
                    onChange={(e) => setRiskPct(Number(e.target.value))}
                    className={`font-mono text-sm h-10 ${isLight ? "bg-white border-gray-300 text-gray-900" : "bg-white/[0.04] border-white/10 text-white"
                      }`}
                  />
                  <div className="flex items-center gap-1 mt-1.5">
                    {RISK_PRESETS.map((r) => (
                      <button
                        key={r}
                        onClick={() => setRiskPct(r)}
                        className={`text-[10px] px-2 py-0.5 rounded font-mono transition-all flex-1 ${riskPct === r
                            ? "bg-[#00FF88] text-black font-bold"
                            : isLight
                              ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                              : "bg-white/[0.04] text-gray-400 hover:bg-white/10 hover:text-white"
                          }`}
                      >
                        {r}%
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* 3. Trade Entry, SL, Target */}
              <div className="pt-2">
                <label className={`block text-xs font-mono uppercase font-semibold mb-2 ${isLight ? "text-gray-700" : "text-gray-300"}`}>
                  4. Setup Execution Levels (Prices in ₹)
                </label>
                <div className="grid grid-cols-3 gap-3 font-mono">
                  {/* Entry */}
                  <div className={`${isLight ? "bg-slate-50 border-slate-300" : "bg-white/[0.02] border-white/[0.08]"} border rounded-xl p-3`}>
                    <p className={`text-[10px] uppercase ${isLight ? "text-slate-700" : "text-gray-400"} font-bold mb-1`}>Entry Price</p>
                    <Input
                      type="number"
                      step="0.5"
                      value={entryPrice}
                      onChange={(e) => setEntryPrice(Number(e.target.value))}
                      className={`h-9 font-bold text-base ${isLight ? "bg-white border-slate-300 text-slate-900 focus:border-sky-500 shadow-sm" : "bg-transparent border-white/10 text-white"}`}
                    />
                  </div>
                  {/* Stop Loss */}
                  <div className={`${isLight ? "bg-rose-50/60 border-rose-200" : "bg-[#FF3B30]/[0.05] border-[#FF3B30]/20"} border rounded-xl p-3`}>
                    <p className={`text-[10px] uppercase ${isLight ? "text-rose-700" : "text-[#FF3B30]"} font-bold mb-1`}>Stop Loss (SL)</p>
                    <Input
                      type="number"
                      step="0.5"
                      value={slPrice}
                      onChange={(e) => setSlPrice(Number(e.target.value))}
                      className={`h-9 font-bold text-base ${isLight ? "bg-white border-rose-300 text-rose-700 shadow-sm" : "bg-transparent border-[#FF3B30]/30 text-[#FF3B30]"}`}
                    />
                    <p className={`text-[9px] ${isLight ? "text-rose-600 font-semibold" : "text-[#FF3B30]/80"} mt-1`}>
                      -{positionResults.pointRisk.toFixed(2)} pts risk
                    </p>
                  </div>
                  {/* Target */}
                  <div className={`${isLight ? "bg-emerald-50/60 border-emerald-200" : "bg-[#00FF88]/[0.05] border-[#00FF88]/20"} border rounded-xl p-3`}>
                    <p className={`text-[10px] uppercase ${isLight ? "text-emerald-700" : "text-[#00FF88]"} font-bold mb-1`}>Target Price</p>
                    <Input
                      type="number"
                      step="0.5"
                      value={targetPrice}
                      onChange={(e) => setTargetPrice(Number(e.target.value))}
                      className={`h-9 font-bold text-base ${isLight ? "bg-white border-emerald-300 text-emerald-800 shadow-sm" : "bg-transparent border-[#00FF88]/30 text-[#00FF88]"}`}
                    />
                    <p className={`text-[9px] ${isLight ? "text-emerald-700 font-semibold" : "text-[#00FF88]/80"} mt-1`}>
                      +{positionResults.pointReward.toFixed(2)} pts reward
                    </p>
                  </div>
                </div>
              </div>

              {/* Pro Explainer Box */}
              <div
                className={`p-3.5 rounded-xl border flex items-start gap-2.5 ${isLight ? "bg-indigo-50/60 border-indigo-100" : "bg-[#00F0FF]/[0.04] border-[#00F0FF]/15"
                  }`}
              >
                <Info className="w-4 h-4 text-[#00F0FF] shrink-0 mt-0.5" />
                <div className="text-xs font-plex leading-relaxed text-gray-300">
                  <span className="font-bold text-[#00F0FF] font-outfit uppercase mr-1">Pro Tip:</span>
                  Never adjust your Stop Loss to fit your desired lot size. Always set your Stop Loss at the technical invalidation level first, then let this calculator compute the safe lot size for you.
                </div>
              </div>
            </div>

            {/* ── Results Output Card (5 Cols) ── */}
            <div className="md:col-span-5 lg:col-span-5 space-y-4">
              <div
                className={`rounded-2xl p-6 border relative overflow-hidden ${isLight
                    ? "bg-white border-gray-200 shadow-lg"
                    : "bg-gradient-to-b from-[#11141E] to-[#0A0C12] border-[#00FF88]/20 shadow-2xl"
                  }`}
              >
                <div className="flex items-center justify-between pb-3 border-b border-white/[0.06]">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#00FF88]" />
                    <h3 className={`text-base font-bold font-outfit ${isLight ? "text-gray-900" : "text-white"}`}>
                      Calculated Sizing Output
                    </h3>
                  </div>
                  <Badge
                    className={`font-mono text-[10px] uppercase font-bold ${positionResults.rrRatio >= 2
                        ? "bg-[#00FF88]/20 text-[#00FF88] border-[#00FF88]/30"
                        : positionResults.rrRatio >= 1.5
                          ? "bg-[#FFCC00]/20 text-[#FFCC00] border-[#FFCC00]/30"
                          : "bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/30"
                      }`}
                  >
                    R:R 1 : {positionResults.rrRatio.toFixed(2)}
                  </Badge>
                </div>

                {/* Hero Lot Output */}
                <div className="py-4 text-center">
                  <p className={`text-xs font-mono uppercase tracking-wider font-semibold ${isLight ? "text-gray-600" : "text-gray-400"}`}>
                    Recommended Order Size for {selectedStock.symbol}
                  </p>
                  <div className="flex items-baseline justify-center gap-2 mt-1">
                    {positionResults.isEquity ? (
                      <>
                        <span className={`text-4xl md:text-5xl font-black font-jetbrains tracking-tight ${isLight ? "text-emerald-700" : "text-[#00FF88]"}`}>
                          {positionResults.recommendedQty}
                        </span>
                        <span className={`text-lg font-bold font-outfit ${isLight ? "text-gray-900" : "text-white"}`}>
                          Shares
                        </span>
                        <span className={`text-xs font-mono font-bold ${isLight ? "text-sky-700" : "text-[#00F0FF]"}`}>
                          (Cash Equity)
                        </span>
                      </>
                    ) : (
                      <>
                        <span className={`text-4xl md:text-5xl font-black font-jetbrains tracking-tight ${isLight ? "text-emerald-700" : "text-[#00FF88]"}`}>
                          {positionResults.recommendedLots}
                        </span>
                        <span className={`text-lg font-bold font-outfit ${isLight ? "text-gray-900" : "text-white"}`}>
                          Lots
                        </span>
                        <span className={`text-sm font-mono font-medium ${isLight ? "text-gray-600" : "text-gray-400"}`}>
                          ({positionResults.recommendedQty} Total Qty • Lot: {positionResults.lotSize})
                        </span>
                      </>
                    )}
                  </div>
                  <p className={`text-[11px] font-plex mt-1 font-medium ${isLight ? "text-gray-600" : "text-gray-400"}`}>
                    Based on strictly risking max ₹{positionResults.maxRiskAmount.toLocaleString("en-IN")} ({riskPct}% capital)
                  </p>
                </div>

                {/* Stats 4-Grid */}
                <div className="grid grid-cols-2 gap-2.5 font-mono pt-2">
                  <div className={`rounded-xl p-3 border ${isLight ? "bg-gray-50 border-gray-200" : "bg-white/[0.03] border-white/[0.06]"}`}>
                    <p className={`text-[9px] uppercase font-bold ${isLight ? "text-gray-600" : "text-gray-400"}`}>Required Capital</p>
                    <p className={`text-sm font-bold mt-0.5 ${isLight ? "text-gray-900" : "text-white"}`}>
                      ₹{positionResults.orderValue.toLocaleString("en-IN")}
                    </p>
                    <p className={`text-[9px] mt-0.5 font-bold ${isLight ? "text-sky-700" : "text-[#00F0FF]"}`}>
                      {positionResults.capitalUtilizationPct.toFixed(1)}% of capital
                    </p>
                  </div>

                  <div className={`rounded-xl p-3 border ${isLight ? "bg-gray-50 border-gray-200" : "bg-white/[0.03] border-white/[0.06]"}`}>
                    <p className={`text-[9px] uppercase font-bold ${isLight ? "text-gray-600" : "text-gray-400"}`}>Point Spread</p>
                    <p className={`text-sm font-bold mt-0.5 ${isLight ? "text-gray-900" : "text-white"}`}>
                      {positionResults.pointRisk.toFixed(1)} SL / {positionResults.pointReward.toFixed(1)} Tgt
                    </p>
                    <p className={`text-[9px] mt-0.5 font-semibold ${isLight ? "text-gray-600" : "text-gray-400"}`}>
                      1 : {positionResults.rrRatio.toFixed(2)} ratio
                    </p>
                  </div>

                  <div className="bg-[#FF3B30]/[0.05] border border-[#FF3B30]/20 rounded-xl p-3">
                    <p className="text-[9px] uppercase text-[#FF3B30] font-bold">Max Loss (If SL Hit)</p>
                    <p className="text-base font-black text-[#FF3B30] mt-0.5">
                      -₹{positionResults.actualRisk.toLocaleString("en-IN")}
                    </p>
                    <p className="text-[9px] text-[#FF3B30]/80 mt-0.5 font-semibold">
                      -{positionResults.lossOnCapitalPct.toFixed(2)}% of account
                    </p>
                  </div>

                  <div className={`rounded-xl p-3 border ${isLight ? "bg-emerald-50/70 border-emerald-200" : "bg-[#00FF88]/[0.05] border-[#00FF88]/20"}`}>
                    <p className={`text-[9px] uppercase font-bold ${isLight ? "text-emerald-700" : "text-[#00FF88]"}`}>Max Gain (If Target Hit)</p>
                    <p className={`text-base font-black mt-0.5 ${isLight ? "text-emerald-700" : "text-[#00FF88]"}`}>
                      +₹{positionResults.actualReward.toLocaleString("en-IN")}
                    </p>
                    <p className={`text-[9px] mt-0.5 font-semibold ${isLight ? "text-emerald-800" : "text-[#00FF88]/80"}`}>
                      +{positionResults.returnOnCapitalPct.toFixed(2)}% of account
                    </p>
                  </div>
                </div>

                {/* Capital Utilization Gauge */}
                <div className="mt-4 pt-3 border-t border-white/[0.06] space-y-1.5">
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-gray-400">Capital Sizing Health</span>
                    <span
                      className={`font-bold ${positionResults.isOverLeveraged
                          ? "text-[#FF3B30]"
                          : positionResults.capitalUtilizationPct > 50
                            ? "text-[#FFCC00]"
                            : "text-[#00FF88]"
                        }`}
                    >
                      {positionResults.isOverLeveraged ? "⚠️ OVER-LEVERAGED" : "SAFE POSITION"}
                    </span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-white/[0.05] overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${positionResults.isOverLeveraged
                          ? "bg-[#FF3B30]"
                          : positionResults.capitalUtilizationPct > 50
                            ? "bg-[#FFCC00]"
                            : "bg-[#00FF88]"
                        }`}
                      style={{ width: `${Math.min(100, positionResults.capitalUtilizationPct)}%` }}
                    />
                  </div>
                </div>

                {/* Copy Plan CTA */}
                <Button
                  onClick={copyTradePlan}
                  className={`w-full mt-5 font-outfit font-bold text-xs h-10 rounded-xl border transition-all flex items-center justify-center gap-2 ${isLight ? "bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-300" : "bg-white/[0.05] hover:bg-white/10 text-white border-white/10"}`}
                >
                  {copiedPlan ? <Check className={`w-4 h-4 ${isLight ? "text-emerald-700" : "text-[#00FF88]"}`} /> : <Copy className={`w-4 h-4 ${isLight ? "text-sky-700" : "text-[#00F0FF]"}`} />}
                  {copiedPlan ? "Trade Plan Copied!" : "Copy Formatted Trade Plan"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB 2: RISK TO REWARD & EV EXPECTANCY MATRIX
            ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === "expectancy" && (
          <div className="grid grid-cols-1 md:grid-cols-12 lg:grid-cols-12 gap-6">
            <div
              className={`md:col-span-6 lg:col-span-6 rounded-2xl p-6 border ${isLight ? "bg-white border-gray-200" : "bg-[#0E1017] border-white/[0.08]"
                } space-y-5`}
            >
              <div className="flex items-center gap-2 pb-3 border-b border-white/[0.06]">
                <Scale className="w-4 h-4 text-[#00F0FF]" />
                <h3 className={`text-base font-bold font-outfit ${isLight ? "text-gray-900" : "text-white"}`}>
                  Mathematical Expectancy Parameters
                </h3>
              </div>

              {/* Win rate slider */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-mono uppercase font-semibold text-gray-300">
                    Estimated Strategy Win Rate (%)
                  </label>
                  <span className="text-sm font-black font-mono text-[#00FF88]">{winRate}%</span>
                </div>
                <input
                  type="range"
                  min="20"
                  max="85"
                  step="1"
                  value={winRate}
                  onChange={(e) => setWinRate(Number(e.target.value))}
                  className="w-full accent-[#00FF88] cursor-pointer"
                />
                <div className="flex justify-between text-[10px] font-mono text-gray-500 mt-1">
                  <span>20% (Low)</span>
                  <span>45% (Realistic Trend)</span>
                  <span>60% (High Pro)</span>
                  <span>85% (Unrealistic)</span>
                </div>
              </div>

              {/* R:R Ratio */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-mono uppercase font-semibold text-gray-300">
                    Average Risk-to-Reward Ratio
                  </label>
                  <span className="text-sm font-black font-mono text-[#00F0FF]">1 : {rrExpectancy}</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[1.5, 2.0, 2.5, 3.0].map((rr) => (
                    <button
                      key={rr}
                      onClick={() => setRrExpectancy(rr)}
                      className={`p-2 rounded-xl border text-center font-mono text-xs font-bold transition-all ${rrExpectancy === rr
                          ? "bg-[#00F0FF]/15 border-[#00F0FF] text-[#00F0FF]"
                          : "bg-white/[0.02] border-white/[0.06] text-gray-400 hover:text-white"
                        }`}
                    >
                      1 : {rr}
                    </button>
                  ))}
                </div>
              </div>

              {/* Risk per trade & Sample */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-xs font-mono uppercase font-semibold mb-1 ${isLight ? "text-slate-700" : "text-gray-300"}`}>
                    Base Risk Per Trade (₹)
                  </label>
                  <Input
                    type="number"
                    value={baseRisk}
                    onChange={(e) => setBaseRisk(Math.max(500, Number(e.target.value)))}
                    className={`font-mono text-sm ${isLight ? "bg-white border-slate-300 text-slate-900" : "bg-white/[0.04] border-white/10 text-white"}`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-mono uppercase font-semibold mb-1 ${isLight ? "text-slate-700" : "text-gray-300"}`}>
                    Sample Simulation Trades
                  </label>
                  <Input
                    type="number"
                    value={sampleTrades}
                    onChange={(e) => setSampleTrades(Math.max(10, Number(e.target.value)))}
                    className={`font-mono text-sm ${isLight ? "bg-white border-slate-300 text-slate-900" : "bg-white/[0.04] border-white/10 text-white"}`}
                  />
                </div>
              </div>

              <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/[0.06] text-xs font-plex text-gray-300 leading-relaxed">
                <span className="font-bold text-[#00FF88] font-mono">Formula:</span> Expected Value (EV) = (Win % × Avg Profit) - (Loss % × Avg Loss). If EV &gt; 0, you possess an institutional mathematical edge.
              </div>
            </div>

            {/* Results */}
            <div className="md:col-span-6 lg:col-span-6 space-y-4">
              <div
                className={`rounded-2xl p-6 border ${isLight ? "bg-white border-gray-200" : "bg-[#0E1017] border-white/[0.08]"
                  } space-y-4`}
              >
                <div className="flex items-center justify-between pb-3 border-b border-white/[0.06]">
                  <h3 className={`text-base font-bold font-outfit ${isLight ? "text-gray-900" : "text-white"}`}>
                    Expectancy Simulation Output
                  </h3>
                  <Badge
                    className={`font-mono text-[10px] ${expectancyResults.evPerTrade > 0 ? "bg-[#00FF88]/20 text-[#00FF88]" : "bg-[#FF3B30]/20 text-[#FF3B30]"
                      }`}
                  >
                    {expectancyResults.evPerTrade > 0 ? "+EV Profitable Edge" : "-EV Losing System"}
                  </Badge>
                </div>

                <div className="text-center py-4">
                  <p className="text-xs font-mono uppercase text-gray-400">Expected Value Per Single Trade</p>
                  <p
                    className={`text-4xl font-black font-jetbrains mt-1 ${expectancyResults.evPerTrade >= 0 ? "text-[#00FF88]" : "text-[#FF3B30]"
                      }`}
                  >
                    {expectancyResults.evPerTrade >= 0 ? "+" : ""}₹{expectancyResults.evPerTrade.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-[11px] font-plex text-gray-400 mt-1">
                    Every trade you take statistically deposits ₹{Math.abs(expectancyResults.evPerTrade).toFixed(0)} into your account long-term.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2.5 font-mono">
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3">
                    <p className="text-[9px] uppercase text-gray-400">Total Simulated P&L ({sampleTrades} Trades)</p>
                    <p
                      className={`text-lg font-black mt-0.5 ${expectancyResults.totalExpectedProfit >= 0 ? "text-[#00FF88]" : "text-[#FF3B30]"
                        }`}
                    >
                      {expectancyResults.totalExpectedProfit >= 0 ? "+" : ""}₹{expectancyResults.totalExpectedProfit.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </p>
                  </div>

                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3">
                    <p className="text-[9px] uppercase text-gray-400">Breakeven Win Rate</p>
                    <p className="text-lg font-black text-[#00F0FF] mt-0.5">
                      {expectancyResults.breakevenWinRate.toFixed(1)}%
                    </p>
                    <p className="text-[9px] text-gray-400">Needed for zero loss</p>
                  </div>

                  <div className="bg-[#00FF88]/[0.05] border border-[#00FF88]/20 rounded-xl p-3">
                    <p className="text-[9px] uppercase text-[#00FF88]">Gross Wins ({expectancyResults.winningTrades} Trades)</p>
                    <p className="text-sm font-bold text-[#00FF88] mt-0.5">
                      +₹{expectancyResults.totalGrossWins.toLocaleString("en-IN")}
                    </p>
                  </div>

                  <div className="bg-[#FF3B30]/[0.05] border border-[#FF3B30]/20 rounded-xl p-3">
                    <p className="text-[9px] uppercase text-[#FF3B30]">Gross Losses ({expectancyResults.losingTrades} Trades)</p>
                    <p className="text-sm font-bold text-[#FF3B30] mt-0.5">
                      -₹{expectancyResults.totalGrossLosses.toLocaleString("en-IN")}
                    </p>
                  </div>
                </div>

                {/* Insight */}
                <div className="p-3 bg-[#FFCC00]/[0.06] border border-[#FFCC00]/20 rounded-xl text-xs font-plex text-gray-300">
                  <span className="text-[#FFCC00] font-bold font-mono">💡 Mathematical Truth:</span> Even if you lose 60 out of 100 trades (40% win rate), with a 1:2.5 R:R you make substantial profit (+₹{((40 * baseRisk * 2.5) - (60 * baseRisk)).toLocaleString("en-IN")}).
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB 3: BROKERAGE & TAX CALCULATOR
            ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === "brokerage" && (
          <div className="grid grid-cols-1 md:grid-cols-12 lg:grid-cols-12 gap-6">
            <div
              className={`md:col-span-6 lg:col-span-6 rounded-2xl p-6 border ${isLight ? "bg-white border-gray-200" : "bg-[#0E1017] border-white/[0.08]"
                } space-y-5`}
            >
              <div className="flex items-center gap-2 pb-3 border-b border-white/[0.06]">
                <Coins className="w-4 h-4 text-[#FFCC00]" />
                <h3 className={`text-base font-bold font-outfit ${isLight ? "text-gray-900" : "text-white"}`}>
                  Government Taxes & Brokerage Breakdown
                </h3>
              </div>

              {/* Segment */}
              <div>
                <label className="block text-xs font-mono uppercase font-semibold mb-2 text-gray-300">
                  Trading Segment
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-xs">
                  {["OPTIONS", "FUTURES", "EQUITY_INTRADAY", "EQUITY_DELIVERY"].map((seg) => (
                    <button
                      key={seg}
                      onClick={() => setCalcSegment(seg)}
                      className={`p-2 rounded-xl border text-center font-bold transition-all ${calcSegment === seg
                          ? "bg-[#FFCC00]/15 border-[#FFCC00] text-[#FFCC00]"
                          : "bg-white/[0.02] border-white/[0.06] text-gray-400 hover:text-white"
                        }`}
                    >
                      {seg.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prices */}
              <div className="grid grid-cols-3 gap-3 font-mono">
                <div>
                  <label className={`block text-[10px] uppercase font-bold mb-1 ${isLight ? "text-slate-700" : "text-gray-400"}`}>Buy Price (₹)</label>
                  <Input
                    type="number"
                    value={bBuyPrice}
                    onChange={(e) => setBBuyPrice(Number(e.target.value))}
                    className={`text-sm font-bold ${isLight ? "bg-white border-slate-300 text-slate-900" : "bg-white/[0.04] border-white/10 text-white"}`}
                  />
                </div>
                <div>
                  <label className={`block text-[10px] uppercase font-bold mb-1 ${isLight ? "text-slate-700" : "text-gray-400"}`}>Sell Price (₹)</label>
                  <Input
                    type="number"
                    value={bSellPrice}
                    onChange={(e) => setBSellPrice(Number(e.target.value))}
                    className={`text-sm font-bold ${isLight ? "bg-white border-slate-300 text-slate-900" : "bg-white/[0.04] border-white/10 text-white"}`}
                  />
                </div>
                <div>
                  <label className={`block text-[10px] uppercase font-bold mb-1 ${isLight ? "text-slate-700" : "text-gray-400"}`}>Total Quantity</label>
                  <Input
                    type="number"
                    value={bQuantity}
                    onChange={(e) => setBQuantity(Number(e.target.value))}
                    className={`text-sm font-bold ${isLight ? "bg-white border-slate-300 text-slate-900" : "bg-white/[0.04] border-white/10 text-white"}`}
                  />
                </div>
              </div>

              {/* Dhan Zero Brokerage Banner */}
              <div className={`p-3 rounded-xl border flex items-center justify-between ${isLight ? "bg-emerald-50 border-emerald-200" : "bg-[#00FF88]/[0.05] border-[#00FF88]/20"}`}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className={`w-4 h-4 ${isLight ? "text-emerald-700" : "text-[#00FF88]"}`} />
                  <span className={`text-xs font-mono font-bold ${isLight ? "text-gray-900" : "text-white"}`}>Dhan Partner Integration</span>
                </div>
                <span className={`text-[10px] font-mono font-bold ${isLight ? "text-emerald-700" : "text-[#00FF88]"}`}>Flat ₹20 / Trade (₹0 Delivery)</span>
              </div>
            </div>

            {/* Tax Breakdown Table */}
            <div className="md:col-span-6 lg:col-span-6 space-y-4">
              <div
                className={`rounded-2xl p-6 border ${isLight ? "bg-white border-gray-200" : "bg-[#0E1017] border-white/[0.08]"
                  } space-y-4`}
              >
                <div className={`flex items-center justify-between pb-3 border-b ${isLight ? "border-gray-200" : "border-white/[0.06]"}`}>
                  <h3 className={`text-base font-bold font-outfit ${isLight ? "text-gray-900" : "text-white"}`}>
                    Net P&L & Charges Statement
                  </h3>
                  <Badge className={`font-mono text-[10px] font-bold ${isLight ? "bg-gray-100 text-gray-800 border border-gray-300" : "bg-white/10 text-white"}`}>
                    Breakeven: +{taxResults.breakevenPoints.toFixed(2)} pts
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-3 font-mono">
                  <div className={`rounded-xl p-3 border ${isLight ? "bg-gray-50 border-gray-200" : "bg-white/[0.03] border-white/[0.06]"}`}>
                    <p className={`text-[9px] uppercase font-bold ${isLight ? "text-gray-600" : "text-gray-400"}`}>Gross P&L</p>
                    <p
                      className={`text-xl font-black mt-0.5 ${taxResults.grossPnl >= 0 ? (isLight ? "text-emerald-700" : "text-[#00FF88]") : "text-[#FF3B30]"
                        }`}
                    >
                      {taxResults.grossPnl >= 0 ? "+" : ""}₹{taxResults.grossPnl.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className={`rounded-xl p-3 border ${isLight ? "bg-gray-50 border-gray-200" : "bg-white/[0.03] border-white/[0.06]"}`}>
                    <p className={`text-[9px] uppercase font-bold ${isLight ? "text-gray-600" : "text-gray-400"}`}>Net Take-Home P&L</p>
                    <p
                      className={`text-xl font-black mt-0.5 ${taxResults.netPnl >= 0 ? (isLight ? "text-emerald-700" : "text-[#00FF88]") : "text-[#FF3B30]"
                        }`}
                    >
                      {taxResults.netPnl >= 0 ? "+" : ""}₹{taxResults.netPnl.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>

                {/* Breakdown line items */}
                <div className="space-y-1.5 font-mono text-xs pt-2">
                  <div className={`flex justify-between py-1 border-b ${isLight ? "border-gray-200" : "border-white/[0.04]"}`}>
                    <span className={isLight ? "text-gray-700 font-medium" : "text-gray-400"}>Brokerage (Buy + Sell)</span>
                    <span className={`font-bold ${isLight ? "text-gray-900" : "text-white"}`}>₹{taxResults.brokerage.toFixed(2)}</span>
                  </div>
                  <div className={`flex justify-between py-1 border-b ${isLight ? "border-gray-200" : "border-white/[0.04]"}`}>
                    <span className={isLight ? "text-gray-700 font-medium" : "text-gray-400"}>STT (Securities Transaction Tax)</span>
                    <span className={`font-bold ${isLight ? "text-amber-700" : "text-[#FFCC00]"}`}>₹{taxResults.stt.toFixed(2)}</span>
                  </div>
                  <div className={`flex justify-between py-1 border-b ${isLight ? "border-gray-200" : "border-white/[0.04]"}`}>
                    <span className={isLight ? "text-gray-700 font-medium" : "text-gray-400"}>Exchange Turnover Charges</span>
                    <span className={`font-bold ${isLight ? "text-gray-900" : "text-white"}`}>₹{taxResults.etc.toFixed(2)}</span>
                  </div>
                  <div className={`flex justify-between py-1 border-b ${isLight ? "border-gray-200" : "border-white/[0.04]"}`}>
                    <span className={isLight ? "text-gray-700 font-medium" : "text-gray-400"}>GST (18%)</span>
                    <span className={`font-bold ${isLight ? "text-gray-900" : "text-white"}`}>₹{taxResults.gst.toFixed(2)}</span>
                  </div>
                  <div className={`flex justify-between py-1 border-b ${isLight ? "border-gray-200" : "border-white/[0.04]"}`}>
                    <span className={isLight ? "text-gray-700 font-medium" : "text-gray-400"}>SEBI & Stamp Duty</span>
                    <span className={`font-bold ${isLight ? "text-gray-900" : "text-white"}`}>₹{(taxResults.sebi + taxResults.stamp).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-1.5 font-bold text-sm text-[#FF3B30] pt-2">
                    <span>Total Taxes & Charges</span>
                    <span>-₹{taxResults.totalTaxes.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB 4: CAPITAL SHIELD & DRAWDOWN
            ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === "drawdown" && (
          <div className="grid grid-cols-1 md:grid-cols-12 lg:grid-cols-12 gap-6">
            <div
              className={`md:col-span-6 lg:col-span-6 rounded-2xl p-6 border ${isLight ? "bg-white border-gray-200" : "bg-[#0E1017] border-white/[0.08]"
                } space-y-5`}
            >
              <div className="flex items-center gap-2 pb-3 border-b border-white/[0.06]">
                <Shield className="w-4 h-4 text-[#00FF88]" />
                <h3 className={`text-base font-bold font-outfit ${isLight ? "text-gray-900" : "text-white"}`}>
                  Account Capital Shield Configuration
                </h3>
              </div>

              <div>
                <label className={`block text-xs font-mono uppercase font-semibold mb-1 ${isLight ? "text-slate-700" : "text-gray-300"}`}>
                  Total Trading Balance (₹)
                </label>
                <Input
                  type="number"
                  value={shieldCapital}
                  onChange={(e) => setShieldCapital(Number(e.target.value))}
                  className={`font-mono text-sm ${isLight ? "bg-white border-slate-300 text-slate-900" : "bg-white/[0.04] border-white/10 text-white"}`}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-xs font-mono uppercase font-semibold mb-1 ${isLight ? "text-gray-700" : "text-gray-300"}`}>
                    Max Drawdown Limit (%)
                  </label>
                  <Input
                    type="number"
                    value={maxDrawdownPct}
                    onChange={(e) => setMaxDrawdownPct(Number(e.target.value))}
                    className={`font-mono text-sm ${isLight ? "bg-white border-gray-300 text-gray-900" : "bg-white/[0.04] border-white/10 text-white"}`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-mono uppercase font-semibold mb-1 ${isLight ? "text-gray-700" : "text-gray-300"}`}>
                    Daily Loss Lock Limit (%)
                  </label>
                  <Input
                    type="number"
                    value={dailyLossLimitPct}
                    onChange={(e) => setDailyLossLimitPct(Number(e.target.value))}
                    className={`font-mono text-sm ${isLight ? "bg-white border-gray-300 text-gray-900" : "bg-white/[0.04] border-white/10 text-white"}`}
                  />
                </div>
              </div>

              <div>
                <label className={`block text-xs font-mono uppercase font-semibold mb-1 ${isLight ? "text-gray-700" : "text-gray-300"}`}>
                  Risk Per Single Trade (%)
                </label>
                <Input
                  type="number"
                  step="0.5"
                  value={riskPerTradePct}
                  onChange={(e) => setRiskPerTradePct(Number(e.target.value))}
                  className={`font-mono text-sm ${isLight ? "bg-white border-gray-300 text-gray-900" : "bg-white/[0.04] border-white/10 text-white"}`}
                />
              </div>
            </div>

            <div className="md:col-span-6 lg:col-span-6 space-y-4">
              <div
                className={`rounded-2xl p-6 border ${isLight ? "bg-white border-gray-200" : "bg-[#0E1017] border-white/[0.08]"
                  } space-y-4`}
              >
                <h3 className={`text-base font-bold font-outfit pb-3 border-b ${isLight ? "border-gray-200 text-gray-900" : "border-white/[0.06] text-white"}`}>
                  Drawdown Resilience Metric
                </h3>

                <div className="grid grid-cols-2 gap-3 font-mono">
                  <div className={`rounded-xl p-3 border ${isLight ? "bg-emerald-50 border-emerald-200" : "bg-[#00FF88]/[0.05] border-[#00FF88]/20"}`}>
                    <p className={`text-[9px] uppercase font-bold ${isLight ? "text-emerald-800" : "text-[#00FF88]"}`}>Consecutive Losses Tolerance</p>
                    <p className={`text-3xl font-black mt-1 ${isLight ? "text-emerald-700" : "text-[#00FF88]"}`}>{shieldResults.consecutiveLosses}</p>
                    <p className={`text-[9px] mt-0.5 ${isLight ? "text-gray-600 font-medium" : "text-gray-400"}`}>SL hits survived before {maxDrawdownPct}% drawdown</p>
                  </div>

                  <div className="bg-[#FF3B30]/[0.05] border border-[#FF3B30]/20 rounded-xl p-3">
                    <p className="text-[9px] uppercase text-[#FF3B30] font-bold">Max Daily Loss Cap</p>
                    <p className="text-3xl font-black text-[#FF3B30] mt-1">₹{shieldResults.dailyLossAmt.toLocaleString("en-IN")}</p>
                    <p className={`text-[9px] mt-0.5 ${isLight ? "text-gray-600 font-medium" : "text-gray-400"}`}>Terminal shutdown threshold</p>
                  </div>
                </div>

                <div className={`p-4 rounded-xl space-y-2 border ${isLight ? "bg-gray-50 border-gray-200" : "bg-white/[0.02] border-white/[0.06]"}`}>
                  <p className={`text-xs font-bold font-outfit uppercase ${isLight ? "text-sky-700" : "text-[#00F0FF]"}`}>🛡️ Tilt Prevention Protocol</p>
                  <ul className={`text-xs font-plex space-y-1.5 ${isLight ? "text-gray-700 font-medium" : "text-gray-300"}`}>
                    <li>• <strong>Max {shieldResults.maxTradesPerDay} losing trades/day:</strong> If hit, stop trading immediately.</li>
                    <li>• <strong>Never double size after a loss:</strong> Martingale destroys trading accounts.</li>
                    <li>• <strong>Protect capital first:</strong> Profits are a byproduct of survival.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            TAB 5: PRO RULES & GUIDE
            ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === "guide" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                title: "1. The 1% - 2% Golden Rule",
                desc: "Never risk more than 1% to 2% of your total account equity on any single trade. If you have ₹1,00,000, your maximum monetary loss per trade should never exceed ₹1,500 - ₹2,000.",
                tag: "Core Rule",
                icon: Shield,
                color: "#00FF88",
              },
              {
                title: "2. Asymmetric Risk-Reward",
                desc: "Only enter setups offering at least 1:2 or 1:2.5 Risk-to-Reward. This allows you to stay profitable over hundreds of trades even if your win rate is only 40%.",
                tag: "Mathematics",
                icon: Scale,
                color: "#00F0FF",
              },
              {
                title: "3. Stop Loss Before Quantity",
                desc: "Identify your technical Stop Loss level first (e.g. support breakout or swing low). Only then calculate the quantity. Never pick quantity first and adjust SL to fit your capital.",
                tag: "Execution",
                icon: Target,
                color: "#FFCC00",
              },
              {
                title: "4. Option Premium Decay & Theta",
                desc: "Options buying requires strict position sizing because time decay eats premium. Keep maximum position size under 30% of your capital to avoid zero-day wipeouts.",
                tag: "Options Logic",
                icon: Flame,
                color: "#FF9500",
              },
              {
                title: "5. Daily Loss Circuit Breaker",
                desc: "Set a daily max loss limit (usually 3% of capital or 2 consecutive losing trades). When hit, close all terminals and do not revenge trade.",
                tag: "Psychology",
                icon: AlertTriangle,
                color: "#FF3B30",
              },
              {
                title: "6. Factoring Taxes & Slippages",
                desc: "Government STT and exchange charges consume 5-15% of gross profits in scalping. Always account for breakeven points before rapid in-and-out trades.",
                tag: "Real P&L",
                icon: Coins,
                color: "#A78BFA",
              },
            ].map((rule, idx) => {
              const Icon = rule.icon;
              return (
                <div
                  key={idx}
                  className={`rounded-2xl p-5 border transition-all hover:border-white/20 ${isLight ? "bg-white border-gray-200" : "bg-[#0E1017] border-white/[0.08]"
                    }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${rule.color}15`, border: `1px solid ${rule.color}30` }}
                    >
                      <Icon className="w-4 h-4" style={{ color: rule.color }} />
                    </div>
                    <Badge variant="outline" className="font-mono text-[9px] uppercase" style={{ borderColor: `${rule.color}40`, color: rule.color }}>
                      {rule.tag}
                    </Badge>
                  </div>
                  <h4 className={`text-sm font-bold font-outfit mb-2 ${isLight ? "text-gray-900" : "text-white"}`}>
                    {rule.title}
                  </h4>
                  <p className={`text-xs font-plex leading-relaxed ${isLight ? "text-gray-600" : "text-[#94A3B8]"}`}>
                    {rule.desc}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
